/**
 * Page /sound — Réglages Son & Annonces vocales (serveur), accessible depuis /manage.
 *
 * Centralise TOUTE la configuration sonore de l'affichage — auparavant éparpillée dans
 * des contrôles ad hoc de l'écran /display. L'écran /display ne fait plus qu'AFFICHER ;
 * ces réglages sont persistés dans la config serveur (PUT /api/config) et s'appliquent à
 * tous les postes d'affichage.
 *
 * Réglages : bip d'appel (on/off + nombre), annonces vocales multilingues (AR/FR/EN :
 * activation, voix installée, débit) avec boutons de test. La résolution finale de la voix
 * se fait sur le poste /display contre les voix réellement installées (repli automatique).
 *
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useConfig } from '../../contexts/ConfigContext';

const LANGS = [
  { key: 'fr', label: 'Français', locale: 'fr', sample: 'Ticket numéro A 12, veuillez vous présenter au guichet numéro 3.' },
  { key: 'ar', label: 'العربية', locale: 'ar', sample: 'تذكرة رقم أ 12، يرجى التوجه إلى الشباك رقم 3.' },
  { key: 'en', label: 'English', locale: 'en', sample: 'Ticket number A 12, please proceed to counter number 3.' },
];

function SoundSettings() {
  const navigate = useNavigate();
  const { config, updateConfig, loading } = useConfig();

  const [beepEnabled, setBeepEnabled] = useState(true);
  const [beepCount, setBeepCount] = useState(1);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [langs, setLangs] = useState({});
  const [voices, setVoices] = useState([]);
  const [voiceSource, setVoiceSource] = useState('display'); // 'display' | 'local'
  const [saving, setSaving] = useState(false);

  // Charge la config existante dans le formulaire dès qu'elle est disponible.
  useEffect(() => {
    if (!config) return;
    setBeepEnabled(config.display?.beepEnabled !== false);
    setBeepCount(Number(config.display?.beepCount) || 1);
    setVoiceEnabled(config.voice?.enabled !== false);
    const vl = config.voice?.languages || {};
    const next = {};
    LANGS.forEach(({ key, locale }) => {
      const l = vl[key] || {};
      next[key] = {
        enabled: l.enabled ?? (key !== 'en'),
        locale: l.locale || `${locale}-${locale === 'ar' ? 'SA' : locale === 'fr' ? 'FR' : 'US'}`,
        voiceURI: l.voiceURI || '',
        rate: Number(l.rate) || 1,
        pitch: Number(l.pitch) || 1,
        volume: Number(l.volume) || 1,
      };
    });
    setLangs(next);
  }, [config]);

  // Source des voix = le POSTE D'AFFICHAGE (remontées par socket au serveur), PAS le
  // navigateur qui édite. Indispensable quand on configure depuis une console distante
  // (Ubuntu) alors que /display tourne sur Windows : les voix diffèrent. Repli sur les
  // voix locales uniquement si le poste n'a encore rien remonté (ex. /display pas ouvert).
  useEffect(() => {
    let alive = true;
    axios.get('/api/display/voices')
      .then((r) => {
        if (!alive) return;
        const list = r?.data?.data || [];
        if (list.length) { setVoices(list); setVoiceSource('display'); }
        else if ('speechSynthesis' in window) {
          setVoices(window.speechSynthesis.getVoices() || []);
          setVoiceSource('local');
        }
      })
      .catch(() => {
        if (alive && 'speechSynthesis' in window) {
          setVoices(window.speechSynthesis.getVoices() || []);
          setVoiceSource('local');
        }
      });
    return () => { alive = false; };
  }, []);

  const voicesByLang = useMemo(() => {
    const map = { fr: [], ar: [], en: [] };
    voices.forEach((v) => {
      const p = (v.lang || '').slice(0, 2).toLowerCase();
      if (map[p]) map[p].push(v);
    });
    return map;
  }, [voices]);

  const setLangField = (key, field, value) => {
    setLangs((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  // Les tests sont joués SUR LE POSTE D'AFFICHAGE (là où sont les haut-parleurs de la salle),
  // pas sur la machine de configuration : le serveur relaie l'ordre au(x) poste(s) /display.
  const testBeep = useCallback(async () => {
    try {
      await axios.post('/api/display/test', { kind: 'beep' });
      toast.info("Bip envoyé au poste d'affichage");
    } catch { toast.error("Poste d'affichage injoignable"); }
  }, []);

  const testVoice = useCallback(async (key) => {
    const cfg = langs[key];
    const meta = LANGS.find((l) => l.key === key);
    try {
      await axios.post('/api/display/test', {
        kind: 'voice',
        text: meta.sample,
        locale: cfg?.locale || meta.locale,
        voiceURI: cfg?.voiceURI || '',
        rate: Number(cfg?.rate) || 1,
      });
      toast.info(`Annonce ${meta.label} envoyée au poste d'affichage`);
    } catch { toast.error("Poste d'affichage injoignable"); }
  }, [langs]);

  const handleSave = async () => {
    setSaving(true);
    // Fusion NON destructive : on repart de la config existante et on n'écrase que
    // les branches son (display.beep* et voice.*).
    const merged = JSON.parse(JSON.stringify(config || {}));
    merged.display = merged.display || {};
    merged.display.beepEnabled = beepEnabled;
    merged.display.beepCount = Number(beepCount) || 1;
    merged.voice = merged.voice || {};
    merged.voice.enabled = voiceEnabled;
    merged.voice.order = LANGS.filter((l) => langs[l.key]?.enabled).map((l) => l.key);
    merged.voice.languages = merged.voice.languages || {};
    LANGS.forEach(({ key }) => {
      merged.voice.languages[key] = { ...(merged.voice.languages[key] || {}), ...langs[key] };
    });
    const ok = await updateConfig(merged);
    setSaving(false);
    if (ok) toast.success('Réglages son enregistrés');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 md:p-10">
      <audio id="sound-test-beep" src="/sounds/ding.mp3" preload="auto" />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">🔊 Son & Annonces vocales</h1>
            <p className="text-sm text-gray-500">Bip d'appel et annonces vocales multilingues (AR/FR/EN) de l'écran d'affichage.</p>
          </div>
          <button onClick={() => navigate('/manage')} className="text-sm text-gray-600 hover:text-gray-900 underline">← Retour</button>
        </div>

        {/* Bip d'appel */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100 mb-6">
          <div className="text-lg font-semibold text-gray-800 mb-3">Bip d'appel</div>
          <label className="flex items-center gap-2 mb-4">
            <input type="checkbox" checked={beepEnabled} onChange={(e) => setBeepEnabled(e.target.checked)} />
            <span className="text-sm text-gray-700">Jouer un bip avant l'annonce vocale</span>
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-700">Nombre de bips</label>
            <input
              type="number" min="0" max="9" value={beepCount}
              onChange={(e) => setBeepCount(e.target.value)}
              className="w-20 px-2 py-1 border rounded" disabled={!beepEnabled}
            />
            <button onClick={testBeep} className="px-3 py-1.5 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-800">▶ Tester le bip</button>
          </div>
        </div>

        {/* Annonces vocales */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100 mb-6">
          <label className="flex items-center gap-2 mb-1">
            <input type="checkbox" checked={voiceEnabled} onChange={(e) => setVoiceEnabled(e.target.checked)} />
            <span className="text-lg font-semibold text-gray-800">Annonces vocales</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            La liste des voix provient du <strong>poste d'affichage</strong> (là où le son est joué),
            pas de cette machine de configuration. Les boutons <em>« Tester »</em> déclenchent l'annonce
            <strong> sur le poste d'affichage</strong>. Si la voix choisie y est absente, un repli
            automatique s'applique (voix de la même langue, sinon muet).
          </p>
          <div className={`text-xs mb-4 px-3 py-2 rounded ${voiceSource === 'display' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            {voiceSource === 'display'
              ? `✔ ${voices.length} voix détectées sur le poste d'affichage.`
              : "⚠ Aucun poste d'affichage n'a encore remonté ses voix (écran /display fermé ?). Liste de repli = voix de CETTE machine, potentiellement différentes du serveur."}
          </div>

          {LANGS.map(({ key, label }) => {
            const cfg = langs[key] || {};
            const list = voicesByLang[key] || [];
            return (
              <div key={key} className={`border rounded-lg p-4 mb-3 ${cfg.enabled ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setLangField(key, 'enabled', e.target.checked)} disabled={!voiceEnabled} />
                    <span className="font-semibold text-gray-800">{label} <span className="text-xs text-gray-400">({cfg.locale})</span></span>
                  </label>
                  <button onClick={() => testVoice(key)} className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700" disabled={!voiceEnabled}>▶ Tester</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Voix ({list.length} installée{list.length > 1 ? 's' : ''})</label>
                    <select
                      value={cfg.voiceURI || ''} onChange={(e) => setLangField(key, 'voiceURI', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm" disabled={!voiceEnabled || !cfg.enabled}
                    >
                      <option value="">Automatique (1re voix {label})</option>
                      {list.map((v) => (
                        <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Débit ({Number(cfg.rate) || 1})</label>
                    <input
                      type="range" min="0.5" max="1.5" step="0.1" value={Number(cfg.rate) || 1}
                      onChange={(e) => setLangField(key, 'rate', Number(e.target.value))}
                      className="w-full" disabled={!voiceEnabled || !cfg.enabled}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Procédure d'installation des voix OS — l'app NE PEUT PAS installer une voix
            (opération système hors navigateur/Electron) ; elle se fait sur la machine qui
            JOUE le son (le poste /display). Voir docs/19-CONSIGNES-VOIX-TTS-MULTILINGUE.md. */}
        <details className="bg-white rounded-xl shadow p-6 border border-gray-100 mb-6">
          <summary className="text-lg font-semibold text-gray-800 cursor-pointer">
            📥 Installer des voix (AR / FR / EN) sur le poste d'affichage
          </summary>
          <div className="mt-4 text-sm text-gray-700 space-y-4">
            <p className="text-gray-500">
              Une voix s'installe sur la <strong>machine qui fait l'annonce</strong> (le poste d'affichage),
              puis on <strong>redémarre le navigateur / l'app</strong>. L'application ne peut pas l'installer
              à votre place : c'est une opération système.
            </p>

            <div>
              <div className="font-semibold text-gray-800 mb-1">🪟 Windows 10 / 11</div>
              <ol className="list-decimal ml-5 space-y-1">
                <li><strong>Paramètres</strong> → <strong>Heure et langue</strong> → <strong>Langue et région</strong>.</li>
                <li><strong>Ajouter une langue</strong> : Arabe (العربية), Français, English selon les besoins.</li>
                <li>Sur la langue → <strong>⋯</strong> → <strong>Options linguistiques</strong> → section <strong>Voix / Parole</strong> → <strong>Télécharger</strong> le pack de voix.</li>
                <li>Alternative : <strong>Paramètres → Heure et langue → Voix / Parole → « Gérer les voix » → Ajouter des voix</strong>.</li>
                <li><strong>Redémarrer</strong> le navigateur / l'app, puis recharger cette page.</li>
              </ol>
              <p className="text-gray-500 mt-1">Voix arabe : parfois via <em>Applications → Fonctionnalités facultatives</em>, ou incluse avec la langue d'affichage arabe.</p>
            </div>

            <div>
              <div className="font-semibold text-gray-800 mb-1">🐧 Linux (Ubuntu / Debian)</div>
              <p className="mb-1">La Web Speech API passe par <strong>speech-dispatcher</strong> + un moteur (eSpeak NG) :</p>
              <pre className="bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto text-xs"><code>sudo apt update
sudo apt install -y speech-dispatcher espeak-ng espeak-ng-data
systemctl --user enable --now speech-dispatcher</code></pre>
              <p className="text-gray-500 mt-1">Puis relancer Chromium/Electron. Voix plus naturelles pour un accueil public : <strong>Piper</strong> (voir la doc).</p>
            </div>

            <p className="text-gray-500">
              Détails complets : <code>docs/19-CONSIGNES-VOIX-TTS-MULTILINGUE.md</code>.
            </p>
          </div>
        </details>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave} disabled={saving || loading}
            className="px-5 py-2.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : '💾 Enregistrer les réglages'}
          </button>
          <button onClick={() => navigate('/manage')} className="px-5 py-2.5 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">Annuler</button>
        </div>
      </div>
    </div>
  );
}

export default SoundSettings;
